import { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet-async';

import BackLinkHeader from 'components/Auth/Common/Header/BackLinkHeader';
import Render from 'components/render';
import {
  HYPERVERGE_SDK_URL,
  IS_RN_WEBVIEW,
  SUPPORT_TICKET_URL,
} from 'constants/constants';
import { DISABLE_2FA_FACE_AUTH_ENABLED } from 'constants/featureFlag';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import CommonMobileHeader from './CommonMobileHeader';
import { DISABLE_2FA_FLOW } from './Disable2faFaceAuth.constants';
import { storeUserIdFromTransactionId } from './Disable2faFaceAuth.helpers';
import styles from './disable2faFaceAuth.module.scss';
import { WorkflowData } from './Disable2faFaceAuth.types';
import EmailOtpVerification from './EmailOtpVerification';
import PostSdkSuccess from './PostSdkSuccess';
import { RaiseDisable2FATicketInfo } from './RaiseDisable2FATicketInfo/RaiseDisable2FATicketInfo';
import Reset2faComplete from './Reset2faComplete';
import { SelfieVerificationFailed } from './SelfieVerificationFailed/SelfieVerificationFailed';
import { SelfieVerificationRejected } from './SelfieVerificationFailed/SelfieVerificationRejected';
import VerifyEmailPass from './VerifyEmailPass';
import VerifyFaceAuth from './VerifyFaceAuth';
import VerifyOauth from './VerifyUsingOauth/VerifyOauth';

const textDisable2fa = i18n.t('auth:twoFaFaceAuth.disable2fa');

const textEmailVerification = i18n.t('auth:emailVerification');

const textVerifyIdentityToReset2fa = i18n.t(
  'auth:twoFaFaceAuth.verifyIdentityToReset2fa'
);

const textRaiseSupportTicket = i18n.t('common:raiseSupportTicket');

const headerTitles = {
  [DISABLE_2FA_FLOW.EMAIL_OTP_VERIFICATION]: textEmailVerification,
  [DISABLE_2FA_FLOW.FACE_AUTH]: textVerifyIdentityToReset2fa,
  [DISABLE_2FA_FLOW.EMAIL_VERIFICATION]: textDisable2fa,
  [DISABLE_2FA_FLOW.RAISE_TICKET]: textRaiseSupportTicket,
};

interface RenderActiveFlowProps {
  activeFlow: DISABLE_2FA_FLOW;
  workflowData: WorkflowData | null;
  setActiveFlow: (flow: DISABLE_2FA_FLOW) => void;
  setWorkflowData: (data: WorkflowData | null) => void;
  raiseTicketMessage: string | null;
}

const RenderActiveFlow = ({
  activeFlow,
  workflowData,
  setActiveFlow,
  setWorkflowData,
  raiseTicketMessage,
}: RenderActiveFlowProps) => {
  const handlePostSdkSuccess = useCallback(() => {
    if (workflowData?.transactionId) {
      storeUserIdFromTransactionId(workflowData.transactionId);
    }

    setActiveFlow(DISABLE_2FA_FLOW.RESET_2FA_DONE);

    trackMixpanelEvent(
      MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH.LOST_2FA_FACE_AUTH_STATUS,
      {
        status: 'success',
        source: MIXPANEL_SOURCES.LOGIN_PAGE,
      }
    );
  }, [workflowData, setActiveFlow]);

  const handlePostSdkError = useCallback(
    reason => {
      setActiveFlow(
        reason === 'time_out'
          ? DISABLE_2FA_FLOW.FACE_AUTH_FAILED
          : DISABLE_2FA_FLOW.FACE_AUTH_REJECTED
      );

      trackMixpanelEvent(
        MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH.LOST_2FA_FACE_AUTH_STATUS,
        {
          status: reason,
          source: MIXPANEL_SOURCES.LOGIN_PAGE,
        }
      );
    },
    [setActiveFlow]
  );

  switch (activeFlow) {
    case DISABLE_2FA_FLOW.EMAIL_VERIFICATION:
      return (
        <>
          <Box as="h2" className={styles.twofaHeading}>
            {textDisable2fa}
          </Box>
          <VerifyOauth
            updateActiveFlow={setActiveFlow}
            updateWorkflowData={setWorkflowData}
          />
          <VerifyEmailPass
            onSuccess={() => {
              setActiveFlow(DISABLE_2FA_FLOW.EMAIL_OTP_VERIFICATION);

              trackMixpanelEvent(
                MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH
                  .EMAIL_VERIFIICATION_SUCCESS,
                {
                  type: 'Face Auth',
                  source: MIXPANEL_SOURCES.LOGIN_PAGE,
                  verification_type: 'email',
                }
              );
            }}
            updateActiveFlow={setActiveFlow}
          />
        </>
      );

    case DISABLE_2FA_FLOW.FACE_AUTH:
      return (
        <VerifyFaceAuth updateActiveFlow={setActiveFlow} workflowData={workflowData!} />
      );
    case DISABLE_2FA_FLOW.EMAIL_OTP_VERIFICATION:
      return (
        <EmailOtpVerification
          updateActiveFlow={setActiveFlow}
          updateWorkflowData={setWorkflowData}
        />
      );

    case DISABLE_2FA_FLOW.FACE_AUTH_FAILED:
      return <SelfieVerificationFailed className={styles.selfieFailedContainer} />;
    case DISABLE_2FA_FLOW.FACE_AUTH_REJECTED:
      return <SelfieVerificationRejected className={styles.selfieFailedContainer} />;
    case DISABLE_2FA_FLOW.SDK_SUCCESS:
      return (
        <PostSdkSuccess
          onError={handlePostSdkError}
          onSuccess={handlePostSdkSuccess}
          // workflow data will be present before reaching this so assertion is fine
          recoveryId={workflowData!.recoveryId!}
        />
      );
    case DISABLE_2FA_FLOW.RAISE_TICKET:
      return (
        (<RaiseDisable2FATicketInfo
          className={styles.raiseTicketContainer}
          heading={
            <div className={styles.raiseTicketHeading} data-palette="RenderActiveFlow">{textRaiseSupportTicket}</div>
          }
          description={raiseTicketMessage}
          btnSection={
            <DeltaButton
              data-testid="raise-support-ticket-lost-2fa"
              text="Raise Support Ticket"
              fullWidth
              as="a"
              className={styles.linkBtn}
              href={SUPPORT_TICKET_URL}
              target={IS_RN_WEBVIEW ? '_self' : '_blank'}
            />
          }
        />)
      );
    case DISABLE_2FA_FLOW.RESET_2FA_DONE:
      return <Reset2faComplete />;
    default:
      return null;
  }
};

const Disable2faFaceAuth = () => {
  const [activeFlow, setActiveFlow] = useState<DISABLE_2FA_FLOW>(
    DISABLE_2FA_FACE_AUTH_ENABLED
      ? DISABLE_2FA_FLOW.EMAIL_VERIFICATION
      : DISABLE_2FA_FLOW.RAISE_TICKET
  );

  const [raiseTicketMessage, setRaiseTicketMessage] = useState<string | null>(null);

  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);

  const { whenAboveMd } = useCustomMediaQuery();

  const updateActiveFlow = useCallback(
    (flow: DISABLE_2FA_FLOW, options?: { message?: string }) => {
      setActiveFlow(flow);
      setRaiseTicketMessage(options?.message || null);
    },
    [setActiveFlow]
  );

  return (<>
    <Helmet>
      <script src={HYPERVERGE_SDK_URL} async />
    </Helmet>
    <Render when={whenAboveMd}>
      <BackLinkHeader text={i18n.t('common:back')} backLink={routes.login} />
    </Render>
    <div className={styles.container} data-palette="Disable2faFaceAuth">
      <CommonMobileHeader
        title={headerTitles[activeFlow] ?? ''}
        testId="disable-2fa-post-verify-header"
      />
      <RenderActiveFlow
        activeFlow={activeFlow}
        workflowData={workflowData}
        setActiveFlow={updateActiveFlow}
        setWorkflowData={setWorkflowData}
        raiseTicketMessage={raiseTicketMessage}
      />
    </div>
  </>);
};
export default Disable2faFaceAuth;
